import { useId } from 'react';
import { Link } from 'react-router-dom';
import { RepeatIcon, ArrowRightIcon } from 'lucide-react';
import { usePages } from '../../contexts/SiteContext';
import { useAccount } from '../../contexts/AccountContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useFeatures } from '../../hooks/useFeatures';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnOnClay, btnPrimary } from '../../ui/styles';
import { Section } from './Section';
type Props = {
  /**
   * `full`: belofte, drie stappen en de voordelen, op klei: het ene diepe vlak van de webshop.
   * `short`: dezelfde woorden zonder de stappen, licht (shell), voor pagina's die hun klei al elders
   * gebruiken (Protocollen), met een link naar het complete protocol bovenaan. Nooit anders
   * geformuleerd: alles komt uit Paginateksten › Abonnement.
   */
  size?: 'full' | 'short';
};
const CTA = 'Stel je abonnement samen';
const MANAGE = 'Pas je abonnement aan';
const MANAGE_PATH = '/account/thuis#levering';

/**
 * Het abonnement: één bron voor elke pagina. De knop opent de samensteller (een lade). Loopt er
 * al een abonnement (ingelogd), dan gaat hij naar Mijn omgeving: zo begin je nooit per ongeluk
 * een tweede. Op klei alleen `text-shell` (5,6:1); blush alleen voor accenten.
 */
export function SubscriptionBlock({
  size = 'full'
}: Props) {
  const {
    subscription
  } = usePages();
  const headingId = useId();
  const {
    account
  } = useAccount();
  const {
    openSubscriptionBuilder
  } = useOverlayActions();
  // Abonnement uitgezet (Instellingen › Koppelingen & privacy › Onderdelen): het blok staat nergens.
  const {
    subscriptions
  } = useFeatures();
  if (!subscriptions) return null;
  const running = Boolean(account?.subscription && account.subscription.status !== 'gestopt');
  const perks = subscription.perks.map(p => p.title).filter(Boolean).join(' · ');
  const action = (cls: string) => running ? <Link to={MANAGE_PATH} className={cls} data-id="element-9099" data-mp="1l5f61gvch5ia">
        {MANAGE}
      </Link> : <button type="button" onClick={openSubscriptionBuilder} aria-haspopup="dialog" className={cls} data-id="element-9100" data-mp="1uyje69iidmyg">
        {CTA}
      </button>;
  if (size === 'short') {
    return <section className="bg-shell py-16 sm:py-20" aria-labelledby={headingId} data-id="element-9101" data-mp="1lymt0hvbv685">
        <div className="mx-auto grid max-w-content gap-8 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-9102" data-mp="1h0bmag0bxbu5">
          <div className="lg:col-span-7" data-id="element-9103" data-mp="1c1i8snl1lktz">
            <h2 id={headingId} className="type-sub text-ink" data-id="element-9104" data-mp="1c2jm6ypj6jev">
              {subscription.claim}
            </h2>
            <p className="mt-3 flex items-center gap-2 text-[14px] text-clay" data-id="element-9105" data-mp="1785j0a4mn7zn">
              <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-9106" data-mp="1396gew6dbnbd" />
              {subscription.name}
            </p>
            <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-9107" data-mp="y7qyve3efyxq">{subscription.intro}</p>
            {subscription.price && <p className="mt-3 text-[14px] text-muted" data-id="element-9108" data-mp="oaw1wzmry6lk">{subscription.price}</p>}
          </div>
          <div className="flex flex-col items-start gap-4 lg:col-span-5 lg:items-end" data-id="element-9109" data-mp="e7z2pxyv52k2">
            {action(btnPrimary)}
            {!running && <a href="#anchor-routine" className={`${quietLink} text-muted hover:text-ink`} data-id="element-9110" data-mp="j4pi0j2ufu4f">
                Of zet het complete protocol op bijvullen
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-9111" data-mp="t59ivkmgawm4" />
              </a>}
          </div>
        </div>
      </section>;
  }
  return <Section tone="clay" labelledBy={headingId} contained={false} data-id="element-9112" data-mp="fsvx2hlo7xa">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-9113" data-mp="dybdpa8qhtq">
        <div className="lg:col-span-5" data-id="element-9114" data-mp="5d2imadlu5hf">
          <h2 id={headingId} className={sectionHeading} data-id="element-9115" data-mp="a6du4otd3afo">
            {subscription.claim}
          </h2>
          <p className="mt-4 flex items-center gap-2 text-[14px] text-shell" data-id="element-9116" data-mp="38wnptsgf6hz8">
            <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-9117" data-mp="3dup8wdduihap" />
            {subscription.name}
          </p>
          <p className="mt-6 max-w-md text-[17px] leading-relaxed text-shell" data-id="element-9118" data-mp="1jfs6g0foa893">{subscription.intro}</p>
          {subscription.price && <p className="mt-5 text-[14px] text-shell" data-id="element-9119" data-mp="1eh8jpxt98sfo">{subscription.price}</p>}
          <div className="mt-9" data-id="element-9120" data-mp="1t5lfousceba6">{action(btnOnClay)}</div>
        </div>

        {/* Een echte volgorde, dus genummerd: zo werkt het. De voordelen als één regel eronder. */}
        <div className="self-center lg:col-span-7" data-id="element-9121" data-mp="224yjp7xp67ni">
          <ol className="border-t border-shell/30" data-id="element-9122" data-mp="223f0b8gqvghi">
            {subscription.steps.map((step, i) => <li key={step.title} className="grid grid-cols-[2.5rem_1fr] border-b border-shell/30 py-6" data-id="element-9123" data-mp="1y3d4vh1lrjao" data-mpi={i}>
                <span aria-hidden="true" className="type-name font-light tabular-nums text-shell" data-id="element-9124" data-mp="2c1bigk83nbp4">
                  {i + 1}
                </span>
                <div data-id="element-9125" data-mp="holgrgbhoy76">
                  <h3 className="type-name font-light text-shell" data-id="element-9126" data-mp="hpwua7uqlkbh">{step.title}</h3>
                  {step.body && <p className="mt-1.5 text-[15px] leading-relaxed text-shell" data-id="element-9127" data-mp="crlhbo95atp1">{step.body}</p>}
                </div>
              </li>)}
          </ol>
          {perks && <p className="mt-6 text-[14px] leading-relaxed text-shell" data-id="element-9128" data-mp="2td00ul9xcomx">{perks}</p>}
        </div>
      </div>
    </Section>;
}